import React, { useEffect, useState } from 'react';
import { adminAPI } from '../../lib/api';
import { formatDateTime, getInitials } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { HeadphonesIcon, Loader2, Send, MessageSquare, AlertCircle, CheckCircle, Clock, Plus } from 'lucide-react';
import { AttachmentUploader, AttachmentPreview } from '../../components/support/SupportAttachment';

export default function AdminSupport() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyAttachment, setReplyAttachment] = useState(null);
  const [sending, setSending] = useState(false);
  // Admin-initiated message to a landlord
  const [newMsgOpen, setNewMsgOpen] = useState(false);
  const [landlords, setLandlords] = useState([]);
  const [loadingLandlords, setLoadingLandlords] = useState(false);
  const [sendingMsg, setSendingMsg] = useState(false);
  const [newMsg, setNewMsg] = useState({ landlord_id: '', subject: '', priority: 'normal', message: '' });
  const [recipientType, setRecipientType] = useState('landlord');
  const [newMsgAttachment, setNewMsgAttachment] = useState(null);

  const loadRecipients = async (role) => {
    setLoadingLandlords(true);
    try {
      const res = await adminAPI.getSupportRecipients(role);
      setLandlords(res.data || []);
    } catch {
      toast.error('Failed to load recipients');
    } finally {
      setLoadingLandlords(false);
    }
  };

  const openNewMessage = async () => {
    setNewMsg({ landlord_id: '', subject: '', priority: 'normal', message: '' });
    setNewMsgAttachment(null);
    setRecipientType('landlord');
    setNewMsgOpen(true);
    loadRecipients('landlord');
  };

  const handleRecipientTypeChange = (role) => {
    setRecipientType(role);
    setNewMsg((p) => ({ ...p, landlord_id: '' }));
    loadRecipients(role);
  };

  const handleSendNewMessage = async () => {
    if (!newMsg.landlord_id) { toast.error('Select a recipient'); return; }
    if (!newMsg.message.trim()) { toast.error('Enter a message'); return; }
    setSendingMsg(true);
    try {
      await adminAPI.createOutreachTicket({
        recipient_id: newMsg.landlord_id,
        recipient_role: recipientType,
        subject: newMsg.subject.trim() || 'Message from Support',
        priority: newMsg.priority,
        message: newMsg.message.trim(),
        ...(newMsgAttachment || {}),
      });
      toast.success('Message sent');
      setNewMsgOpen(false);
      loadTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingMsg(false);
    }
  };

  useEffect(() => {
    loadTickets();
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps -- intentional mount-only load

  const loadTickets = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getSupportTickets(filter === 'all' ? null : filter);
      setTickets(response.data);
    } catch (error) {
      console.error('Error loading tickets:', error);
      toast.error('Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleViewTicket = async (ticketId) => {
    try {
      const response = await adminAPI.getSupportTicket(ticketId);
      setSelectedTicket(response.data);
      // server clears admin unread on view; reflect in list + sidebar
      setTickets((prev) => prev.map((t) => (t.ticket_id === ticketId ? { ...t, unread_for_admin: false } : t)));
      window.dispatchEvent(new Event('badge-refresh'));
    } catch (error) {
      toast.error('Failed to load ticket details');
    }
  };

  const handleSendReply = async () => {
    if ((!replyText.trim() && !replyAttachment) || !selectedTicket) return;

    setSending(true);
    try {
      await adminAPI.replyToTicket(selectedTicket.ticket_id, {
        message: replyText,
        ...(replyAttachment || {}),
      });
      toast.success('Reply sent');
      setReplyText('');
      setReplyAttachment(null);
      handleViewTicket(selectedTicket.ticket_id);
      loadTickets();
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleUpdateStatus = async (ticketId, status) => {
    try {
      await adminAPI.updateTicketStatus(ticketId, status);
      toast.success(`Ticket marked as ${status}`);
      if (selectedTicket?.ticket_id === ticketId) {
        handleViewTicket(ticketId);
      }
      loadTickets();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <AlertCircle className="w-4 h-4" />;
      case 'in_progress': return <Clock className="w-4 h-4" />;
      case 'resolved': return <CheckCircle className="w-4 h-4" />;
      case 'closed': return <CheckCircle className="w-4 h-4" />;
      default: return <MessageSquare className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'in_progress': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'resolved': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'closed': return 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 dark:bg-gray-800/50 dark:text-gray-400';
      default: return 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900 dark:text-red-300 dark:border-red-800';
      case 'high': return 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900 dark:text-orange-300 dark:border-orange-800';
      case 'normal': return 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900 dark:text-blue-300 dark:border-blue-800';
      case 'low': return 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700';
      default: return 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300';
    }
  };

  // Stats
  const openCount = tickets.filter(t => t.status === 'open').length;
  const inProgressCount = tickets.filter(t => t.status === 'in_progress').length;
  const resolvedCount = tickets.filter(t => t.status === 'resolved').length;

  if (loading && tickets.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-support-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold">Support Center</h1>
          <p className="text-muted-foreground mt-1">Manage landlord support requests and reach out directly</p>
        </div>
        <Button onClick={openNewMessage} className="bg-[#C85A42] hover:bg-[#B04A35]" data-testid="admin-new-message-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Message
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('open')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Open</p>
                <p className="text-2xl font-bold">{openCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('in_progress')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">In Progress</p>
                <p className="text-2xl font-bold">{inProgressCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('resolved')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Resolved</p>
                <p className="text-2xl font-bold">{resolvedCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tickets List */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <HeadphonesIcon className="w-5 h-5" />
              Support Tickets
            </CardTitle>
            <CardDescription>Click on a ticket to view details and respond</CardDescription>
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tickets</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {tickets.length === 0 ? (
            <div className="text-center py-12">
              <HeadphonesIcon className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No support tickets found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {tickets.map((ticket) => (
                <div
                  key={ticket.ticket_id}
                  className="p-4 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => handleViewTicket(ticket.ticket_id)}
                  data-testid={`ticket-${ticket.ticket_id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={ticket.user?.picture} />
                        <AvatarFallback className="bg-[#C85A42] text-white">
                          {getInitials(ticket.user?.name || ticket.user_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {ticket.unread_for_admin && (
                            <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" data-testid={`ticket-unread-${ticket.ticket_id}`} title="New reply" />
                          )}
                          <p className={`truncate ${ticket.unread_for_admin ? 'font-bold' : 'font-medium'}`}>{ticket.subject}</p>
                          <Badge variant="outline" className={getPriorityColor(ticket.priority)}>
                            {ticket.priority}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-1">{ticket.message}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {ticket.user?.name || ticket.user_name} • {formatDateTime(ticket.created_at)}
                        </p>
                      </div>
                    </div>
                    <Badge className={`${getStatusColor(ticket.status)} shrink-0`}>
                      {getStatusIcon(ticket.status)}
                      <span className="ml-1 capitalize">{ticket.status.replace('_', ' ')}</span>
                    </Badge>
                  </div>
                  {ticket.replies?.length > 0 && (
                    <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                      <MessageSquare className="w-4 h-4" />
                      {ticket.replies.length} {ticket.replies.length === 1 ? 'reply' : 'replies'}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ticket Detail Dialog */}
      <Dialog open={!!selectedTicket} onOpenChange={(open) => !open && setSelectedTicket(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          {selectedTicket && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <DialogTitle className="text-xl">{selectedTicket.subject}</DialogTitle>
                    <div className="flex items-center gap-2 mt-2">
                      <Avatar className="w-6 h-6">
                        <AvatarImage src={selectedTicket.user?.picture} />
                        <AvatarFallback className="bg-[#C85A42] text-white text-xs">
                          {getInitials(selectedTicket.user?.name || selectedTicket.user_name)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm text-muted-foreground">{selectedTicket.user?.name || selectedTicket.user_name}</span>
                      <Badge variant="outline" className={getPriorityColor(selectedTicket.priority)}>
                        {selectedTicket.priority}
                      </Badge>
                    </div>
                  </div>
                  <Select 
                    value={selectedTicket.status} 
                    onValueChange={(val) => handleUpdateStatus(selectedTicket.ticket_id, val)}
                  >
                    <SelectTrigger className="w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </DialogHeader>

              <div className="flex-1 overflow-y-auto space-y-4 py-4">
                {/* Original Message */}
                <div className="p-4 bg-muted/50 rounded-lg">
                  {selectedTicket.created_by_admin && (
                    <Badge variant="default" className="mb-2 text-xs">Sent by Support</Badge>
                  )}
                  <p className="text-sm whitespace-pre-wrap">{selectedTicket.message}</p>
                  <AttachmentPreview
                    url={selectedTicket.attachment_url}
                    name={selectedTicket.attachment_name}
                    type={selectedTicket.attachment_type}
                    testid="admin-ticket-original-attachment"
                  />
                  <p className="text-xs text-muted-foreground mt-2">{formatDateTime(selectedTicket.created_at)}</p>
                </div>

                {/* Replies */}
                {selectedTicket.replies?.map((reply) => (
                  <div
                    key={reply.reply_id}
                    className={`p-4 rounded-lg ${
                      reply.user_role === 'admin' 
                        ? 'bg-[#C85A42]/10 ml-8' 
                        : 'bg-muted/50 mr-8'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant={reply.user_role === 'admin' ? 'default' : 'secondary'} className="text-xs">
                        {reply.user_role === 'admin' ? 'Admin' : ({ tenant: 'Tenant', property_manager: 'Property Manager' }[reply.user_role] || 'Landlord')}
                      </Badge>
                      <span className="text-xs text-muted-foreground">{formatDateTime(reply.created_at)}</span>
                    </div>
                    {reply.message && <p className="text-sm whitespace-pre-wrap">{reply.message}</p>}
                    <AttachmentPreview url={reply.attachment_url} name={reply.attachment_name} type={reply.attachment_type} />
                  </div>
                ))}
              </div>

              {/* Reply Input */}
              {selectedTicket.status !== 'closed' && (
                <div className="border-t pt-4 space-y-3">
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your reply..."
                    rows={3}
                    data-testid="admin-reply-input"
                  />
                  <div className="flex justify-between items-center gap-2">
                    <AttachmentUploader
                      attachment={replyAttachment}
                      onChange={setReplyAttachment}
                      uploadFn={adminAPI.uploadSupportAttachment}
                      testidPrefix="admin-reply"
                    />
                    <Button
                      onClick={handleSendReply}
                      disabled={sending || (!replyText.trim() && !replyAttachment)}
                      className="bg-[#C85A42] hover:bg-[#B04A35]"
                      data-testid="admin-send-reply-btn"
                    >
                      {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                      Send Reply
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* New Message to Landlord Dialog */}
      <Dialog open={newMsgOpen} onOpenChange={setNewMsgOpen}>
        <DialogContent data-testid="admin-new-message-dialog">
          <DialogHeader>
            <DialogTitle>Message a Recipient</DialogTitle>
            <DialogDescription>
              Start a conversation with a landlord, tenant, or property manager. It appears on their Support page and they can reply.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Recipient Type *</Label>
              <Select value={recipientType} onValueChange={handleRecipientTypeChange}>
                <SelectTrigger data-testid="new-message-recipient-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="landlord">Landlord</SelectItem>
                  <SelectItem value="tenant">Tenant</SelectItem>
                  <SelectItem value="property_manager">Property Manager</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Recipient *</Label>
              <Select value={newMsg.landlord_id} onValueChange={(v) => setNewMsg((p) => ({ ...p, landlord_id: v }))}>
                <SelectTrigger data-testid="new-message-landlord-select">
                  <SelectValue placeholder={loadingLandlords ? 'Loading…' : 'Select a recipient'} />
                </SelectTrigger>
                <SelectContent>
                  {landlords.map((l) => (
                    <SelectItem key={l.user_id} value={l.user_id}>
                      {l.name} ({l.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2 col-span-2 sm:col-span-1">
                <Label>Subject</Label>
                <Input
                  value={newMsg.subject}
                  onChange={(e) => setNewMsg((p) => ({ ...p, subject: e.target.value }))}
                  placeholder="Message from Support"
                  data-testid="new-message-subject"
                />
              </div>
              <div className="space-y-2 col-span-2 sm:col-span-1">
                <Label>Priority</Label>
                <Select value={newMsg.priority} onValueChange={(v) => setNewMsg((p) => ({ ...p, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Message *</Label>
              <Textarea
                value={newMsg.message}
                onChange={(e) => setNewMsg((p) => ({ ...p, message: e.target.value }))}
                placeholder="Write your message to the landlord..."
                rows={5}
                data-testid="new-message-content"
              />
            </div>
            <div>
              <AttachmentUploader
                attachment={newMsgAttachment}
                onChange={setNewMsgAttachment}
                uploadFn={adminAPI.uploadSupportAttachment}
                testidPrefix="new-message"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewMsgOpen(false)}>Cancel</Button>
            <Button onClick={handleSendNewMessage} disabled={sendingMsg} className="bg-[#C85A42] hover:bg-[#B04A35]" data-testid="new-message-send-btn">
              {sendingMsg ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Send Message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
